import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';

import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import { Checkbox, Input } from '@components/common';
import type { ApiError } from '@app-types';
import type { SystemParameter } from '@app-types/system-parameter';
import { systemParameterService } from '@services';
import { showToast } from '@utils/toast';
import styles from './SystemParametersSettings.module.css';

function formatParameterKey(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').trim();
}

function validateParameterValue(parameter: SystemParameter, value: string): string | null {
  if (value.trim() === '') {
    return 'Value is required';
  }

  if (parameter.type === 'Number') {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || !/^\d+$/.test(value.trim())) {
      return 'Enter a whole number';
    }
    if (numericValue < 1) {
      return 'Value must be at least 1';
    }
  }

  return null;
}

const SystemParametersSettings: React.FC = () => {
  const [parameters, setParameters] = useState<SystemParameter[]>([]);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const loadParameters = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const data = await systemParameterService.getSystemParameters();
      setParameters(data);
      setDraftValues(Object.fromEntries(data.map((parameter) => [parameter.key, parameter.value])));
      setFieldErrors({});
    } catch {
      setLoadError(true);
      showToast.error('Failed to load system parameters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadParameters();
  }, [loadParameters]);

  const handleValueChange = (key: string, value: string) => {
    setDraftValues((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSave = async (parameter: SystemParameter) => {
    const draftValue = draftValues[parameter.key] ?? parameter.value;
    const validationError = validateParameterValue(parameter, draftValue);

    if (validationError) {
      setFieldErrors((prev) => ({ ...prev, [parameter.key]: validationError }));
      return;
    }

    try {
      setSavingKey(parameter.key);
      const updated = await systemParameterService.updateSystemParameter(parameter.key, draftValue.trim());
      setParameters((prev) => prev.map((item) => (item.key === updated.key ? updated : item)));
      setDraftValues((prev) => ({ ...prev, [updated.key]: updated.value }));
      setFieldErrors((prev) => {
        if (!prev[parameter.key]) {
          return prev;
        }
        const next = { ...prev };
        delete next[parameter.key];
        return next;
      });
      showToast.success(`${formatParameterKey(parameter.key)} saved successfully`);
    } catch (error) {
      const apiError = error as ApiError;
      showToast.error(apiError?.response?.data?.error || 'Failed to save system parameter');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading} data-test-id="system-parameters-loading">
        <Loader2 size={48} className="spin" />
        <p>Loading system parameters...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.error} data-test-id="system-parameters-error">
        Failed to load system parameters
      </div>
    );
  }

  return (
    <div className={styles.container} data-test-id="system-parameters-settings">
      <div>
        <h2 className={styles.sectionTitle}>System Parameters</h2>
        <p className={styles.sectionDescription}>
          Configure system-wide settings used by scheduled jobs and automated processes.
        </p>
      </div>

      {parameters.length === 0 ? (
        <div className={styles.empty} data-test-id="system-parameters-empty">
          No system parameters are configured.
        </div>
      ) : (
        <div className={styles.cardList}>
          {parameters.map((parameter) => {
            const draftValue = draftValues[parameter.key] ?? parameter.value;
            const isDirty = draftValue !== parameter.value;
            const isSaving = savingKey === parameter.key;
            const validationError = fieldErrors[parameter.key];
            const isSaveDisabled = !isDirty || isSaving || Boolean(validationError);

            return (
              <div key={parameter.key} className={styles.card} data-test-id={`system-parameter-${parameter.key}`}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>{formatParameterKey(parameter.key)}</h3>
                  {parameter.usedBy && (
                    <Badge variant="info" dataTestId={`system-parameter-used-by-${parameter.key}`}>
                      {parameter.usedBy}
                    </Badge>
                  )}
                </div>

                {parameter.description && <p className={styles.cardDescription}>{parameter.description}</p>}

                <div className={styles.cardActions}>
                  {parameter.type === 'Boolean' ? (
                    <div className={styles.booleanField}>
                      <Checkbox
                        label="Enabled"
                        checked={draftValue === 'true'}
                        onChange={(event) =>
                          handleValueChange(parameter.key, event.target.checked ? 'true' : 'false')
                        }
                        dataTestId={`system-parameter-value-${parameter.key}`}
                      />
                    </div>
                  ) : (
                    <Input
                      label="Value"
                      type={parameter.type === 'Number' ? 'number' : 'text'}
                      min={parameter.type === 'Number' ? 1 : undefined}
                      step={parameter.type === 'Number' ? 1 : undefined}
                      value={draftValue}
                      onChange={(event) => handleValueChange(parameter.key, event.target.value)}
                      error={validationError}
                      wrapperClassName={styles.inputField}
                      dataTestId={`system-parameter-value-${parameter.key}`}
                    />
                  )}

                  <Button
                    variant="primary"
                    onClick={() => void handleSave(parameter)}
                    disabled={isSaveDisabled}
                    loading={isSaving}
                    loadingText="Saving..."
                    dataTestId={`system-parameter-save-${parameter.key}`}
                  >
                    <Save size={16} />
                    Save
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SystemParametersSettings;
